import { useTasks } from 'app/agriculture/queries/tasks/useTasks';
import { useMemo } from 'react';
import Fuse from 'fuse.js';
import { useUsers } from 'app/agriculture/hooks/users/useUsers';
import { useGenericEntityFromId } from 'app/agriculture/queries/entities/useGenericEntityFromId';
import { format } from 'date-fns';

const searchSupportedDateFormats = ['dd/MM/yyyy', 'dd-MM-yyyy', 'MMMM yyyy'];

export function useTaskResults(textFilter: string) {
    const tasks = useTasks();
    const users = useUsers();
    const getParent = useGenericEntityFromId();

    const fuse = useMemo(
        () =>
            new Fuse(Array.from(tasks.values()), {
                threshold: 0.3,
                keys: [
                    'title',
                    'category',
                    'priority',
                    { name: 'description', weight: 0.2 },
                    {
                        name: 'parents.name',
                        weight: 0.4,
                        getFn: ({ parentIds }) => parentIds.map((id) => getParent(id)?.name ?? ''),
                    },
                    {
                        name: 'parents.type',
                        weight: 0.2,
                        getFn: ({ parentIds }) => parentIds.map((id) => getParent(id)?.type ?? ''),
                    },
                    {
                        name: 'creator.name',
                        weight: 0.2,
                        getFn: ({ creatorId }) =>
                            users.find((user) => user.id === creatorId)?.name ?? '',
                    },
                    {
                        name: 'assignees.name',
                        weight: 0.3,
                        getFn: ({ assignees }) =>
                            assignees.map((id) => users.find((user) => user.id === id)?.name ?? ''),
                    },
                    {
                        name: 'startDate',
                        weight: 0.3,
                        getFn: ({ startDate }) =>
                            searchSupportedDateFormats.map((dateFormat) =>
                                format(startDate, dateFormat),
                            ),
                    },
                    {
                        name: 'endDate',
                        weight: 0.3,
                        getFn: ({ endDate }) =>
                            searchSupportedDateFormats.map((dateFormat) =>
                                format(endDate, dateFormat),
                            ),
                    },
                ],
            }),
        [tasks, getParent, users],
    );

    return useMemo(
        () =>
            !textFilter
                ? Array.from(tasks.values())
                : fuse.search(textFilter).map(({ item }) => item),
        [textFilter, fuse, tasks],
    );
}
